import React, { useCallback, useMemo } from 'react'
import { useHistory } from 'react-router-dom'

import { isAuthenticated } from '../../../../helpers'
import { Card } from '../../../../pages/calendar/components/Card'
import PassportLogo from '../../../../assets/logo-sm.png'
import starImage from '../../../../assets/svg/Star.svg'
import starGoldImage from '../../../../assets/svg/StarGold.svg'
import { convertDate } from './constants'
import useFollowHandler from './hooks/useFollowHandler'
import { getTournamentStatus } from './helpers'

import styles from './styles.module.scss'

const TournamentItem = ({
  id,
  name,
  registrationEndedAt,
  registrationStartedAt,
  follow,
  game,
  participantsCount,
  prizePool,
}) => {
  const history = useHistory()
  const { star, followHandler } = useFollowHandler(id, name, follow)

  const handleCardClick = useCallback(() => {
    history.push(`/battleCup/${id}/information`)
  }, [history, id])

  const tournamentStatus = useMemo(
    () => getTournamentStatus(registrationStartedAt, registrationEndedAt),
    [registrationStartedAt, registrationEndedAt]
  )

  const gameLogo = useMemo(() => game?.logo || PassportLogo, [game?.logo])
  const gameTitle = useMemo(() => game?.title || 'Game', [game?.title])

  const tournamentTitle = useMemo(() => {
    if (!name) return 'Tournament' + (game?.title ? ` ${game.title}` : '')

    return name
  }, [name, game?.title])

  const tournamentDate = useMemo(
    () => convertDate(registrationStartedAt, registrationEndedAt),
    [registrationStartedAt, registrationEndedAt]
  )

  const starImageSrc = useMemo(() => (star ? starImage : starGoldImage), [star])

  const handleStarClick = useCallback(
    e => {
      e.stopPropagation()
      followHandler()
    },
    [followHandler]
  )

  const tournamentInfo = useMemo(() => {
    if (!participantsCount && !prizePool) return null

    return (
      <div
        className={styles.tournamentInfo}
        data-testid='tournament-participants'
      >
        {participantsCount && (
          <span className={styles.participants}>
            👥 {participantsCount} participants
          </span>
        )}
        {prizePool && (
          <span
            className={styles.prizePool}
            data-testid='tournament-prize-pool'
          >
            💰 ${prizePool}
          </span>
        )}
      </div>
    )
  }, [participantsCount, prizePool])

  const statusClassName = useMemo(() => {
    const statusType = tournamentStatus.text.toLowerCase()

    switch (statusType) {
      case 'upcoming':
        return `${styles.tournamentStatus} ${styles.tournamentStatusUpcoming}`
      case 'open':
        return `${styles.tournamentStatus} ${styles.tournamentStatusOpen}`
      case 'closed':
        return `${styles.tournamentStatus} ${styles.tournamentStatusClosed}`
      default:
        return `${styles.tournamentStatus} ${styles.tournamentStatusUnknown}`
    }
  }, [tournamentStatus.text])

  return (
    <Card
      className={styles.tournament}
      onClick={handleCardClick}
      testId='tournament-card'
    >
      <div className={styles.tournamentHeader}>
        <img
          src={gameLogo}
          className={styles.tournamentLogo}
          alt={gameTitle}
          data-testid='tournament-game-logo'
        />

        {!!isAuthenticated() && (
          <img
            src={starImageSrc}
            className={styles.tournamentStar}
            onClick={handleStarClick}
            alt='Follow tournament'
            data-testid='follow-tournament-button'
          />
        )}
      </div>

      <div className={styles.tournamentContent}>
        <h3 className={styles.tournamentTitle} data-testid='tournament-title'>
          {tournamentTitle}
        </h3>

        <div className={styles.tournamentDate} data-testid='tournament-date'>
          {tournamentDate}
        </div>

        {tournamentInfo}
      </div>

      <div className={statusClassName} data-testid='tournament-status'>
        {tournamentStatus.text}
      </div>
    </Card>
  )
}

export default TournamentItem
